import {
  changeActivitySyncEditStatus,
  changeCrmActivitySyncConfiguration,
  changeCrmActivitySyncStatus,
  changeCrmActivityTypeMapping,
  clearActivitySyncConfig,
  clearCrmActivityTypeMapping,
  fetchCrmActivityConfigs,
  fetchCrmActivityMapping,
  removeCrmActivityTypeMapping,
  saveCrmActivitySyncConfig,
  setCrmActivityTypes,
} from '../../../routines/integrations/crmRoutines';
import {
  checkIfActivitySyncConfigChanged,
  extractActivitySyncConfigs,
  extractActivityTypeMapping,
  setActivityConfigDefaultValues,
  updateCrmActivityTypeStatus,
} from '../../../../common/util/reducerUtils/CrmReducerUtils';
import { INTEGRATION_CRITERIA_TYPE } from '../../../../common/constants/IntegrationConstants';
import {
  CONFIG_STATUS_LEVEL,
  ENTITY_LEVEL,
} from '../../../../common/constants/integrationConstants/crmConstants';

const initialState = {
  activityTypeMapping: [],
  savedActivityTypeMapping: [],
  crmActivityTypes: [],
  integrationConfigs: {
    syncStatus: false,
    historicalSync: false,
    dailySync: false,
    syncType: INTEGRATION_CRITERIA_TYPE.CREATE,
  },
  loading: false,
  error: null,
  hasUnSavedConfig: false,
  savedIntegrationConfigs: {},
  disabled: true,
};

const crmActivitySyncReducer = (state = initialState, action) => {
  switch (action.type) {
    // when updating a crm activity type mapping
    case changeCrmActivityTypeMapping.TRIGGER: {
      const { velarisActivityType, crmActivityType } = action.payload;

      const [existingMapping, otherMappings] = extractActivityTypeMapping(
        velarisActivityType,
        state.activityTypeMapping
      );

      const activityTypeMapping = {
        velaris: velarisActivityType,
        vendor: {
          ...(existingMapping?.vendor || {}),
          [crmActivityType.category]: [
            ...(existingMapping?.vendor[crmActivityType.category] || []),
            crmActivityType.type,
          ],
        },
      };

      const mappedActivityTypes = [...otherMappings, activityTypeMapping];

      const crmActivityTypes = updateCrmActivityTypeStatus(
        mappedActivityTypes,
        state.crmActivityTypes
      );

      const hasChanged = checkIfActivitySyncConfigChanged(
        state.integrationConfigs,
        state.savedIntegrationConfigs,
        mappedActivityTypes,
        state.savedActivityTypeMapping
      );

      return {
        ...state,
        activityTypeMapping: mappedActivityTypes,
        crmActivityTypes,
        hasUnSavedConfig: hasChanged,
      };
    }

    // when clearing all crm activity type mapping for a given velaris activity type
    case clearCrmActivityTypeMapping.TRIGGER: {
      const { velarisActivityType } = action.payload;

      // eslint-disable-next-line no-unused-vars
      const [removedMapping, remainingMappings] = extractActivityTypeMapping(
        velarisActivityType,
        state.activityTypeMapping
      );

      const crmActivityTypes = updateCrmActivityTypeStatus(
        remainingMappings,
        state.crmActivityTypes
      );

      const hasChanged = checkIfActivitySyncConfigChanged(
        state.integrationConfigs,
        state.savedIntegrationConfigs,
        remainingMappings,
        state.savedActivityTypeMapping
      );

      return {
        ...state,
        activityTypeMapping: remainingMappings,
        crmActivityTypes,
        hasUnSavedConfig: hasChanged,
      };
    }

    // when removing a crm activity type mapping for a given velaris activity type
    case removeCrmActivityTypeMapping.TRIGGER: {
      const { velarisActivityType, crmActivityType } = action.payload;

      const { category, type } = crmActivityType;

      const [existingMapping, otherMappings] = extractActivityTypeMapping(
        velarisActivityType,
        state.activityTypeMapping
      );

      const updatedCrmTypes = (existingMapping.vendor[category] || []).filter(
        (mapping) => mapping !== type
      );

      const updatedMapping = {
        ...existingMapping,
        velaris: velarisActivityType,
        vendor: {
          ...(existingMapping.vendor || {}),
        },
      };

      const mappedActivityTypes = [...otherMappings];

      if (updatedCrmTypes.length > 0) {
        updatedMapping.vendor[category] = updatedCrmTypes;
      } else {
        delete updatedMapping.vendor[category];
      }

      if (Object.keys(updatedMapping.vendor).length > 0) {
        mappedActivityTypes.push(updatedMapping);
      }

      const crmActivityTypes = updateCrmActivityTypeStatus(
        mappedActivityTypes,
        state.crmActivityTypes
      );

      const hasChanged = checkIfActivitySyncConfigChanged(
        state.integrationConfigs,
        state.savedIntegrationConfigs,
        mappedActivityTypes,
        state.savedActivityTypeMapping
      );

      return {
        ...state,
        crmActivityTypes,
        activityTypeMapping: mappedActivityTypes,
        hasUnSavedConfig: hasChanged,
      };
    }

    case setCrmActivityTypes.TRIGGER: {
      const { activityTypes } = action.payload;

      const crmActivityTypes = updateCrmActivityTypeStatus(
        state.activityTypeMapping,
        activityTypes
      );

      return {
        ...state,
        crmActivityTypes,
      };
    }

    case fetchCrmActivityConfigs.TRIGGER: {
      return {
        ...state,
        loading: true,
      };
    }

    case fetchCrmActivityConfigs.SUCCESS: {
      const { configs } = action.payload || {};

      const activitySyncConfig = extractActivitySyncConfigs(configs);
      const integrationConfigs =
        setActivityConfigDefaultValues(activitySyncConfig);

      return {
        ...state,
        integrationConfigs,
        savedIntegrationConfigs: activitySyncConfig,
      };
    }

    case changeCrmActivitySyncStatus.TRIGGER: {
      const { status } = action.payload;

      const currentConfig = {
        ...state.integrationConfigs,
        syncStatus: status,
      };

      const hasChanged = checkIfActivitySyncConfigChanged(
        currentConfig,
        state.savedIntegrationConfigs,
        state.activityTypeMapping,
        state.savedActivityTypeMapping
      );

      return {
        ...state,
        integrationConfigs: currentConfig,
        hasUnSavedConfig: hasChanged,
      };
    }

    case changeCrmActivitySyncConfiguration.TRIGGER: {
      const { config } = action.payload;

      const currentConfig = {
        ...state.integrationConfigs,
        ...config,
      };

      const hasChanged = checkIfActivitySyncConfigChanged(
        currentConfig,
        state.savedIntegrationConfigs,
        state.activityTypeMapping,
        state.savedActivityTypeMapping
      );

      return {
        ...state,
        integrationConfigs: currentConfig,
        hasUnSavedConfig: hasChanged,
      };
    }

    case fetchCrmActivityMapping.TRIGGER: {
      return {
        ...state,
        loading: true,
      };
    }

    case fetchCrmActivityMapping.SUCCESS: {
      const { mapping } = action.payload || {};

      const activityMapping = Object.keys(mapping).map((key) => ({
        velaris: Number(key),
        vendor: mapping[key],
      }));

      const crmActivityTypes = updateCrmActivityTypeStatus(
        activityMapping || [],
        state.crmActivityTypes
      );

      return {
        ...state,
        activityTypeMapping: activityMapping,
        savedActivityTypeMapping: activityMapping,
        crmActivityTypes,
        loading: false,
      };
    }

    case saveCrmActivitySyncConfig.TRIGGER: {
      return {
        ...state,
      };
    }

    case saveCrmActivitySyncConfig.SUCCESS: {
      const { config } = action.payload || {};
      const activitySyncConfig = extractActivitySyncConfigs(
        config.integrationConfigs
      );

      const crmActivityTypes = updateCrmActivityTypeStatus(
        config.activityMapping.mappings || [],
        state.crmActivityTypes
      );

      return {
        ...state,
        hasUnSavedConfig: false,
        savedIntegrationConfigs: activitySyncConfig,
        integrationConfigs: activitySyncConfig,
        crmActivityTypes,
        activityTypeMapping: config.activityMapping.mappings,
        savedActivityTypeMapping: config.activityMapping.mappings,
        loading: false,
      };
    }

    case clearActivitySyncConfig.TRIGGER: {
      const crmActivityTypes = updateCrmActivityTypeStatus(
        state.savedActivityTypeMapping,
        state.crmActivityTypes
      );

      return {
        ...state,
        hasUnSavedConfig: false,
        integrationConfigs: state.savedIntegrationConfigs,
        activityTypeMapping: state.savedActivityTypeMapping,
        crmActivityTypes,
      };
    }

    case changeActivitySyncEditStatus.TRIGGER: {
      const { deduplication } = action.payload;

      const isDeduplicationConfigured =
        deduplication[ENTITY_LEVEL.ACCOUNT]?.status ===
          CONFIG_STATUS_LEVEL.SAVED ||
        deduplication[ENTITY_LEVEL.ORGANIZATION]?.status ===
          CONFIG_STATUS_LEVEL.SAVED;
      return {
        ...state,
        disabled: !isDeduplicationConfigured,
      };
    }

    default:
      return state;
  }
};

export default crmActivitySyncReducer;
